/**
 * Dashboard filter seeding helpers.
 *
 * Mental model:
 * - Brand Report popup saves a small set of constraints (stores, weeks, analysis groups).
 * - At page load we pick an "access source" (Brand constraints vs full Retailer catalogs).
 * - We then seed embedded_* Redux bags so each report section/tab gets its own filter keys.
 * - Tab keys follow the section name so one tab's selection never overwrites another.
 * - Store names/ids come from analytics_campaign_stores_list (not active stores / clip list).
 */

import store from "../Store/store";

/** Build the Redux key used by Filters.jsx / defineDashboardVariables for a tab. */
export const filterParamKey = (tabName, suffix) => {
  return `${String(tabName).replace(/ /g, "_")}_${suffix}`;
};

/**
 * Sections that participate in the live report:
 * - impression sections (always considered when present)
 * - performance sections marked visible in report settings
 */
export const getSectionsForFilterSeeding = () => {
  const state = store.getState().userSelections;
  const settings = state.report_settings;

  if (!settings?.available_options) {
    return [];
  }

  const impressionIds = settings.available_options
    .filter((option) => option.type === "impression")
    .map((option) => option.id);

  const visibleIds = settings.visible_options || [];
  const uniqueIds = [...new Set([...impressionIds, ...visibleIds])];

  return uniqueIds
    .map((id) => settings.available_options.find((option) => option.id === id))
    .filter(Boolean);
};

const getSectionFilter = (section, keyword) => {
  return (section.filters || []).find((filter) => filter.keyword === keyword);
};

const sectionHasFilter = (section, keyword) => {
  return Boolean(getSectionFilter(section, keyword));
};

/** Same rule as fetchFirstAnalysisGroup in ReportSettings — skip "(All)", take first. */
export const firstNonAllAnalysisGroup = (analysisGroups = []) => {
  const withoutAll = analysisGroups.filter((item) => item !== "(All)");
  return withoutAll.length > 0 ? withoutAll[0] : "";
};

/** Full store objects loaded for analytics (stores/campaign API). */
export const getAnalyticsCampaignStores = () => {
  const state = store.getState().userSelections;
  return Array.isArray(state.analytics_campaign_stores_list)
    ? state.analytics_campaign_stores_list
    : [];
};

/** Sorted unique store names from the analytics campaign store list. */
export const getAnalyticsCampaignStoreCatalog = () => {
  const names = getAnalyticsCampaignStores().map((store) => store.name).filter(Boolean);
  return [...new Set(names)].sort();
};

/**
 * Map store names → ids using analytics_campaign_stores_list only.
 * Names that are not on the campaign list are skipped (no active-store fallback).
 */
export const findAnalyticsCampaignStoreIds = (storeNames = []) => {
  const names = Array.isArray(storeNames) ? storeNames : [storeNames];
  const campaignStores = getAnalyticsCampaignStores();

  return names
    .map((name) => {
      const match = campaignStores.find((store) => store.name === name);
      return match ? match.id : null;
    })
    .filter((id) => id !== null && id !== undefined && id !== "");
};

/** Keep only names that exist on the analytics campaign store list. */
export const intersectWithAnalyticsStoreCatalog = (storeNames = []) => {
  const catalog = new Set(getAnalyticsCampaignStoreCatalog());
  return (Array.isArray(storeNames) ? storeNames : []).filter((name) => catalog.has(name));
};

/**
 * Catalog defaults from data already loaded into Redux
 * (analytics campaign stores, adlevels, weeks, regions, marketing activities).
 */
export const buildCatalogDefaults = () => {
  const state = store.getState().userSelections;

  const stores = getAnalyticsCampaignStoreCatalog();

  const adlevels =
    state.adlevel_names?.length > 0
      ? state.adlevel_names.map((item) => item.label)
      : [];

  const weeks =
    state.campaign_weeks?.length > 0
      ? state.campaign_weeks
          .map((option) => parseInt(option.value, 10))
          .sort((a, b) => a - b)
      : [];

  const regionLabels =
    state.campaign_regions?.length > 0
      ? state.campaign_regions.map((region) => region.label)
      : [];

  const nielsenCodes =
    state.nielsen_campaign_regions?.length > 0
      ? state.nielsen_campaign_regions.map((region) => region.value)
      : [];

  const activityOptions =
    state.activityOptions?.length > 0 ? [...state.activityOptions].sort() : [];

  return { stores, adlevels, weeks, regionLabels, nielsenCodes, activityOptions };
};

/**
 * Whether this Embeddable bag should seed a single store.
 * Prefers multiSelect when both types appear on the same bag (shared store_ids).
 */
const bagUsesSingleSelectStores = (bagType) => {
  const sections = getSectionsForFilterSeeding().filter((section) =>
    bagType === "impression"
      ? section.type === "impression"
      : section.type !== "impression"
  );

  let hasSingle = false;
  let hasMulti = false;

  sections.forEach((section) => {
    const storesFilter = getSectionFilter(section, "Stores");
    if (!storesFilter) {
      return;
    }
    if (storesFilter.type === "singleSelect") {
      hasSingle = true;
    }
    if (storesFilter.type === "multiSelect") {
      hasMulti = true;
    }
  });

  return hasSingle && !hasMulti;
};

/**
 * Tab-scoped keys only for filters each section actually defines.
 * Respects multiSelect vs singleSelect where it changes the seeded shape.
 */
export const buildTabScopedKeysFromSections = ({
  analysisGroups = [],
  regionLabels = [],
  nielsenCodes = [],
  activityOptions = [],
} = {}) => {
  const impressionKeys = {};
  const performanceKeys = {};

  getSectionsForFilterSeeding().forEach((section) => {
    const target = section.type === "impression" ? impressionKeys : performanceKeys;

    const analysisFilter = getSectionFilter(section, "AnalysisGroup");
    if (analysisFilter) {
      const key = filterParamKey(section.name, "selected_analysis_group");
      if (analysisFilter.type === "singleSelect") {
        const first = firstNonAllAnalysisGroup(analysisGroups);
        target[key] = first ? [first] : [];
      } else {
        // multiSelect (or unspecified) — full Brand/Retailer list
        target[key] = analysisGroups;
      }
    }

    if (sectionHasFilter(section, "Regions")) {
      target[filterParamKey(section.name, "selected_region_name")] = regionLabels;
    }

    if (sectionHasFilter(section, "Nielsen Regions")) {
      target[filterParamKey(section.name, "selected_nielsen_regions")] = nielsenCodes;
    }

    // Newer filters that are not part of the Brand Report popup — seed once at load.
    if (sectionHasFilter(section, "RankBy") && target.rank_by === undefined) {
      target.rank_by = "No Ranking";
    }

    if (sectionHasFilter(section, "AbsolutePercentage")) {
      target[filterParamKey(section.name, "absolute_percentage")] = false;
    }

    // Activity / Activity Type — always single-select string for Embeddable "Activity".
    if (sectionHasFilter(section, "Activity")) {
      if (activityOptions.length > 0) {
        target[filterParamKey(section.name, "marketing_activity")] = activityOptions[0];
      }
    }

    if (sectionHasFilter(section, "Activity Type")) {
      const activityTypeOptions = ["Retailer Promotion", ...activityOptions];
      target[filterParamKey(section.name, "marketing_activity")] = activityTypeOptions[0];
    }

    // Match defineDashboardVariables fallbacks so Redux and Embeddable agree before paint.
    if (sectionHasFilter(section, "Checkout Data")) {
      target[filterParamKey(section.name, "checkout_data")] = "basket_trolley";
    }

    if (sectionHasFilter(section, "absoluteButton")) {
      target[filterParamKey(section.name, "impression_format")] = false;
    }
  });

  return { impressionKeys, performanceKeys };
};

/** Store names → ids + Multiselect option shape used by the dashboard store filters. */
export const buildStoreSelectionFields = (storeNames = []) => {
  const names = intersectWithAnalyticsStoreCatalog(
    Array.isArray(storeNames) ? storeNames : []
  );

  return {
    stores: names,
    store_ids: findAnalyticsCampaignStoreIds(names),
    selected_store_names: names.map((storeName) => ({
      value: storeName,
      label: storeName,
    })),
  };
};

/**
 * Build brand_filters / retailer_filters shapes used by the Brand Report popup
 * and as the access source when seeding embedded bags.
 */
export const buildDefaultAccessFilters = () => {
  const catalog = buildCatalogDefaults();
  const { impressionKeys, performanceKeys } = buildTabScopedKeysFromSections({
    analysisGroups: catalog.adlevels,
    regionLabels: catalog.regionLabels,
    nielsenCodes: catalog.nielsenCodes,
    activityOptions: catalog.activityOptions,
  });

  return {
    impression_filter: {
      stores: catalog.stores,
      ...impressionKeys,
    },
    performance_filter: {
      stores: catalog.stores,
      analysis_group: catalog.adlevels,
      weeks: catalog.weeks,
      ...performanceKeys,
    },
  };
};

/**
 * Turn an access source (Brand or Retailer filter bag) into the two embedded_*
 * objects the dashboard Filters + Embeddable variables read.
 */
export const seedEmbeddedFilters = (accessSource) => {
  const catalog = buildCatalogDefaults();
  const analysisGroups = accessSource?.performance_filter?.analysis_group || catalog.adlevels;

  const { impressionKeys, performanceKeys } = buildTabScopedKeysFromSections({
    analysisGroups,
    regionLabels: catalog.regionLabels,
    nielsenCodes: catalog.nielsenCodes,
    activityOptions: catalog.activityOptions,
  });

  // Brand Report may list stores that are no longer on the campaign — keep only resolvable ones.
  const impressionStores = intersectWithAnalyticsStoreCatalog(
    accessSource?.impression_filter?.stores || catalog.stores
  );
  const performanceStores = intersectWithAnalyticsStoreCatalog(
    accessSource?.performance_filter?.stores || catalog.stores
  );

  // Bag-level store_ids: singleSelect sections get only the first allowed store.
  const impressionStoreFields = bagUsesSingleSelectStores("impression")
    ? buildStoreSelectionFields(impressionStores.slice(0, 1))
    : buildStoreSelectionFields(impressionStores);

  const performanceStoreFields = bagUsesSingleSelectStores("performance")
    ? buildStoreSelectionFields(performanceStores.slice(0, 1))
    : buildStoreSelectionFields(performanceStores);

  return {
    embedded_impression_filter: {
      ...impressionStoreFields,
      ...impressionKeys,
    },
    embedded_performance_filter: {
      ...performanceStoreFields,
      analysis_group: analysisGroups,
      weeks: accessSource?.performance_filter?.weeks || catalog.weeks,
      ...performanceKeys,
    },
  };
};

/**
 * Analysis-group keys for every section that has an AnalysisGroup filter.
 * Used when the Brand Report popup updates analysis groups before save.
 */
export const buildAnalysisGroupKeysForSections = (userChoices) => {
  const { performanceKeys, impressionKeys } = buildTabScopedKeysFromSections({
    analysisGroups: userChoices,
  });

  return { ...impressionKeys, ...performanceKeys };
};

/** Region defaults keyed per section that has a Regions filter. */
export const buildRegionKeysForSections = () => {
  const catalog = buildCatalogDefaults();
  const { impressionKeys, performanceKeys } = buildTabScopedKeysFromSections({
    regionLabels: catalog.regionLabels,
  });
  return { ...impressionKeys, ...performanceKeys };
};

/** Nielsen defaults keyed per section that has a Nielsen Regions filter. */
export const buildNielsenKeysForSections = () => {
  const catalog = buildCatalogDefaults();
  const { impressionKeys, performanceKeys } = buildTabScopedKeysFromSections({
    nielsenCodes: catalog.nielsenCodes,
  });
  return { ...impressionKeys, ...performanceKeys };
};
